/**
 * 무료 PDF — 숨겨진 iframe에 프리뷰 스냅샷을 띄우고 그 iframe에서 print().
 *
 * 왜 iframe인가:
 *   메인 페이지에서 .printing 클래스를 토글해 layout을 reshape한 직후
 *   window.print()를 호출하면, Chrome의 print snapshot이 reshape 이전 layout을
 *   캡쳐하는 race가 일부 환경에서 재현된다. raf 중첩으로도 완전히 막을 수 없음.
 *
 *   iframe은 메인 페이지와 독립된 document/layout을 갖는다. 프리뷰 HTML만 깔끔하게
 *   복제해 넣은 뒤 iframe.contentWindow.print() 를 부르면 메인 페이지 layout과의
 *   race가 원천 차단된다. 결과는 Pro PDF(서버사이드 puppeteer)와 동일한 컨셉.
 */

const PRINT_FRAME_ID = 'mdview-print-frame';

function buildPrintHtml(previewInner: string): string {
  const origin = window.location.origin;

  const headBits: string[] = [
    '<meta charset="UTF-8">',
    `<base href="${origin}/">`,
  ];

  // 현재 페이지의 모든 stylesheet/style/font preload를 iframe head로 복사.
  // base href 덕분에 hashed asset path(/assets/index-XXX.css)와 /fonts/*.woff2가
  // iframe 컨텍스트에서도 정상 해석된다.
  document.head.querySelectorAll('link[rel="stylesheet"]').forEach((l) => {
    headBits.push((l as HTMLLinkElement).outerHTML);
  });
  document.head.querySelectorAll('link[rel="preload"][as="font"]').forEach((l) => {
    headBits.push((l as HTMLLinkElement).outerHTML);
  });
  document.head.querySelectorAll('style').forEach((s) => {
    headBits.push(s.outerHTML);
  });

  // 인쇄 전용 보정. preview의 화면용 max-width/padding 제거 + A4 페이지 룰.
  // 코드/표/수식의 페이지 분할 방지도 여기서 한 번 더 보강.
  headBits.push(`<style>
    @page {
      size: A4;
      margin: 20mm;
      @bottom-center {
        content: counter(page) ' / ' counter(pages);
        font-family: 'Pretendard', system-ui, sans-serif;
        font-size: 10pt;
        color: #6b7280;
      }
    }
    html, body { margin: 0; padding: 0; background: #ffffff; }
    body {
      font-family: 'Pretendard', system-ui, sans-serif;
      color: #111827;
      font-size: 11pt;
      line-height: 1.55;
    }
    .preview { padding: 0 !important; max-width: none !important; margin: 0 !important; }
    .preview pre, .preview table, .preview blockquote,
    .preview .mermaid, .preview .katex-display {
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .preview h1, .preview h2, .preview h3, .preview h4 {
      break-after: avoid;
      page-break-after: avoid;
    }
    /* 표 border — preview.css의 #e5e7eb는 인쇄 시 거의 안 보일 정도로 옅음.
       PDF용으로 한 단계 진한 색 강제 + collapse + thead 배경 보장. */
    .preview table { border-collapse: collapse; width: 100%; }
    .preview th, .preview td {
      border: 1px solid #9ca3af !important;
      padding: 6px 10px;
    }
    .preview thead th { background: #f3f4f6 !important; }
  </style>`);

  return `<!doctype html>
<html lang="ko">
<head>
${headBits.join('\n')}
</head>
<body>
<article class="preview">${previewInner}</article>
</body>
</html>`;
}

export function attachPdfButton(button: HTMLElement): void {
  button.addEventListener('click', () => {
    const preview = document.getElementById('preview');
    if (!preview || !preview.children.length) {
      // 빈 프리뷰는 인쇄할 게 없음 — 조용히 무시.
      return;
    }

    // 이전 인쇄에서 남은 frame이 있으면 먼저 제거 (race 보호).
    document.getElementById(PRINT_FRAME_ID)?.remove();

    const iframe = document.createElement('iframe');
    iframe.id = PRINT_FRAME_ID;
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('title', 'print');
    // viewport 크기는 정상으로 유지해야 한다. width:0/height:0이면 iframe document가
    // 0폭으로 layout되어 표/wide 컨텐츠가 squeezed되거나 안 보인다 (Chrome 인쇄 캡쳐는
    // 일부 시점에 이 layout을 그대로 사용). A4보다 넉넉히 잡고 화면 밖에 숨긴다.
    iframe.style.cssText =
      'position:fixed;left:-99999px;top:0;width:1024px;height:1024px;border:0;visibility:hidden;pointer-events:none;';
    document.body.appendChild(iframe);

    const setup = (): void => {
      const win = iframe.contentWindow;
      const doc = iframe.contentDocument;
      if (!win || !doc) {
        iframe.remove();
        return;
      }

      // afterprint(또는 캔슬)에서 iframe 정리. 사용자가 안 눌러도 새 클릭 때 위에서 제거됨.
      win.addEventListener(
        'afterprint',
        () => {
          iframe.remove();
        },
        { once: true },
      );

      doc.open();
      doc.write(buildPrintHtml(preview.innerHTML));
      doc.close();

      const triggerPrint = (): void => {
        // stylesheet 로딩 보장 — doc.write 직후 readyState가 'complete'로 바뀌어도
        // <link rel="stylesheet">는 비동기 로드 중일 수 있다. 이게 끝나기 전에
        // print()를 부르면 layout snapshot이 stylesheet 미적용 상태로 캡쳐된다
        // (대표적 증상: 표 border 누락).
        const links = Array.from(
          doc.querySelectorAll('link[rel="stylesheet"]'),
        ) as HTMLLinkElement[];
        const ssReady = Promise.all(
          links.map((l) => {
            // 이미 sheet가 노출돼 있으면 로드 완료 — 즉시 resolve.
            if (l.sheet) return Promise.resolve();
            return new Promise<void>((resolve) => {
              const done = (): void => resolve();
              l.addEventListener('load', done, { once: true });
              l.addEventListener('error', done, { once: true });
            });
          }),
        );

        void ssReady.then(() => {
          // CSS 적용 후 layout/paint를 한 번 강제로 굴린다. @font-face는 "사용 시점에
          // 다운로드되는" lazy load이므로 (특히 KaTeX_Main/Math 같은 수식 글리프 폰트),
          // 이 단계 없이 doc.fonts.ready를 부르면 폰트 다운로드 시작 전에 즉시 resolve →
          // print snapshot이 글리프 fallback으로 캡쳐된다 (수식 안 b/2/4ac 등 invisible).
          void doc.body.offsetHeight;
          win.requestAnimationFrame(() => {
            // 이제 폰트 다운로드가 진행 중. fonts.ready가 진짜 로드 완료를 기다린다.
            const fontsReady = doc.fonts ? doc.fonts.ready : Promise.resolve();
            void fontsReady.then(() => {
              win.requestAnimationFrame(() => {
                win.focus();
                win.print();
              });
            });
          });
        });
      };

      if (doc.readyState === 'complete') {
        triggerPrint();
      } else {
        win.addEventListener('load', triggerPrint, { once: true });
      }
    };

    // iframe 자체가 about:blank로 로드되기를 한 번 기다린다. 대부분 즉시 complete.
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      setup();
    } else {
      iframe.addEventListener('load', setup, { once: true });
    }
  });
}
